import React, { useEffect, useState } from 'react';
import {
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Box,
  CssBaseline,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Dashboard as DashboardIcon,
  Inventory2 as ProductsIcon,
  ShoppingCart as CartIcon,
  Campaign as CampaignIcon,
  BarChart as FinancesIcon,
  People as ActivityIcon,
  EmojiEvents as GameContestIcon,
  Send as TicketIcon,
  Assignment as ReportIcon,
  Article as BriefsIcon,
  Cable as ApiConnectIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
} from '@mui/icons-material';

import Logo from '../assets/images/logo.svg';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Badge, Menu, MenuItem, Popover } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getAdminDetailsApi, getPartnerSettingsApi } from '../components/actionCreator';
import { formatUserName } from '../utils/userDisplayName';
import PartnerBadge from '../assets/images/Button.svg';

const headerButtonSx = {
  height: 38,
  px: 1.5,
  borderRadius: '10px',
  border: '1px solid #e6e8ec',
  backgroundColor: '#fff',
  color: '#111827',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: 14,
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { backgroundColor: '#fafafa', boxShadow: 'none' },
};

const dateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const initialHeaderRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return { start: dateString(start), end: dateString(end) };
};

const headerRangeLabel = (start, end) => {
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return '';
  const startLabel = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const endLabel = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startLabel} – ${endLabel}`;
};

const brandFromSettings = (value) => {
  let current = value;
  for (let i = 0; i < 3; i += 1) {
    if (current?.data && typeof current.data === 'object' && !Array.isArray(current.data)) current = current.data;
    else break;
  }
  if (current?.brand && typeof current.brand === 'object') return current.brand;
  return current && typeof current === 'object' ? current : {};
};

const DRAWER_WIDTH_OPEN = 240;
const DRAWER_WIDTH_CLOSED = 72;

const SIDEBAR_NAV = [
  { text: 'Dashboard', icon: DashboardIcon, path: '/dashboard' },
  { text: 'Product stock', icon: ProductsIcon, path: '/products' },
  { text: 'Orders', icon: CartIcon, path: '/usercart' },
  { text: 'Campaign', icon: CampaignIcon, path: '/campaign' },
  { text: 'Finances', icon: FinancesIcon, path: '/finances' },
  { text: 'Activity', icon: ActivityIcon, path: '/activity' },
  { text: 'Game Contest', icon: GameContestIcon, path: '/game-contest' },
  { text: 'Ticket', icon: TicketIcon, path: '/ticket' },
  { text: 'Report', icon: ReportIcon, path: '/report' },
  { text: 'Briefs & Rules', icon: BriefsIcon, path: '/briefs-rules' },
  { text: 'API Connect', icon: ApiConnectIcon, path: '/api-connect' },
  { text: 'Settings', icon: SettingsIcon, path: '/settings' },
  { text: 'Help & Support', icon: HelpIcon, path: '/help-support' },
];

function DashboardLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [desktopOpen, setDesktopOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const showLabels = isDesktop ? desktopOpen : true;
  const drawerPaperWidth = isDesktop
    ? desktopOpen
      ? DRAWER_WIDTH_OPEN
      : DRAWER_WIDTH_CLOSED
    : DRAWER_WIDTH_OPEN;

  const toggleDrawer = () => {
    if (isDesktop) {
      setDesktopOpen((prev) => !prev);
    } else {
      setMobileOpen((prev) => !prev);
    }
  };

  const closeMobileDrawer = () => {
    if (!isDesktop) setMobileOpen(false);
  };

  const isActive = (path) => location.pathname === path;
  const showReportTools =
    location.pathname === '/dashboard' ||
    location.pathname === '/campaign' ||
    location.pathname.startsWith('/campaign/');
  const [anchorEl, setAnchorEl] = useState(null);
  const [storeAnchor, setStoreAnchor] = useState(null);
  const [dateAnchor, setDateAnchor] = useState(null);
  const [brand, setBrand] = useState(null);
  const [headerRange, setHeaderRange] = useState(initialHeaderRange);
  const [draftRange, setDraftRange] = useState(initialHeaderRange);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const [meProfile, setMeProfile] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  useEffect(() => {
    closeMobileDrawer();
  }, [location.pathname, isDesktop]);

  useEffect(() => {
    if (!showReportTools) setDateAnchor(null);
  }, [showReportTools]);

  const loadMyProfile = (onDone) => {
    const id = localStorage.getItem('admin_id');
    if (!id) {
      setMeProfile(null);
      onDone?.();
      return;
    }
    getAdminDetailsApi(id, (res) => {
      if (res?.error || res?.success === false) {
        setMeProfile(null);
        onDone?.();
        return;
      }
      const admin = res?.data !== undefined ? res.data : res;
      setMeProfile(admin && typeof admin === 'object' ? admin : null);
      onDone?.();
    });
  };

  useEffect(() => {
    if (!localStorage.getItem('token') || !localStorage.getItem('admin_id')) return;
    loadMyProfile();
    getPartnerSettingsApi()
      .then((response) => setBrand(brandFromSettings(response)))
      .catch(() => setBrand(null));
  }, []);

  const handleOpenProfile = () => {
    handleMenuClose();
    setProfileDialogOpen(true);
    setProfileLoading(true);
    loadMyProfile(() => setProfileLoading(false));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin_id');
    localStorage.removeItem('role');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('token');
    handleMenuClose();
    navigate('/login');
  };

  const navigateTo = (path) => {
    navigate(path);
    closeMobileDrawer();
  };

  const drawerNav = (
    <>
      <Toolbar />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <List dense={!showLabels}>
          {SIDEBAR_NAV.map((item) => {
            const active = isActive(item.path);
            const Icon = item.icon;
            return (
              <ListItem
                key={item.text}
                button
                onClick={() => navigateTo(item.path)}
                title={!showLabels ? item.text : undefined}
                sx={{
                  pl: showLabels ? 2 : 1.5,
                  pr: showLabels ? 2 : 1,
                  py: 1,
                  minHeight: 48,
                  justifyContent: showLabels ? 'flex-start' : 'center',
                  backgroundColor: active ? '#2A2A2A' : 'transparent',
                  color: active ? 'white' : 'inherit',
                  '&:hover': {
                    backgroundColor: active ? '#2A2A2A' : '#f0f0f0',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: active ? 'white' : 'inherit',
                    minWidth: showLabels ? 56 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <Icon />
                </ListItemIcon>
                {showLabels && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ noWrap: true, fontSize: '0.9375rem' }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundColor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 2.5 }, gap: 1.25 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={toggleDrawer}
            aria-label={(isDesktop ? desktopOpen : mobileOpen) ? 'Close menu' : 'Open menu'}
            sx={{ color: '#111827', mr: 0.25 }}
          >
            {(isDesktop ? desktopOpen : mobileOpen) ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 0 }}>
            <Box component="img" src={Logo} alt="Happer" sx={{ height: { xs: 26, sm: 32 }, width: 'auto' }} />
            <Box
              component="img"
              src={PartnerBadge}
              alt="Partner"
              sx={{ height: 18, width: 'auto', display: { xs: 'none', sm: 'block' } }}
            />
          </Box>
          <Button
            onClick={(event) => setStoreAnchor(event.currentTarget)}
            endIcon={<KeyboardArrowDownIcon sx={{ color: '#6b7280' }} />}
            sx={{ ...headerButtonSx, maxWidth: { xs: 160, sm: 280 }, px: 1 }}
          >
            {brand?.picture ? (
              <Box
                component="img"
                src={brand.picture}
                alt=""
                sx={{ width: 22, height: 22, mr: 1, objectFit: 'contain', flexShrink: 0 }}
              />
            ) : null}
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600, letterSpacing: '0.01em', textTransform: 'uppercase' }}>
              {brand?.name ? `${brand.name} Store` : 'Store'}
            </Box>
          </Button>
          <Menu
            anchorEl={storeAnchor}
            open={Boolean(storeAnchor)}
            onClose={() => setStoreAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <MenuItem onClick={() => setStoreAnchor(null)}>{brand?.name ? `${brand.name} Store` : 'Store'}</MenuItem>
          </Menu>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showReportTools ? (
              <>
                <Button
                  onClick={(event) => {
                    setDraftRange(headerRange);
                    setDateAnchor(event.currentTarget);
                  }}
                  startIcon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18, color: '#6b7280' }} />}
                  sx={{ ...headerButtonSx, display: { xs: 'none', md: 'inline-flex' } }}
                >
                  {headerRangeLabel(headerRange.start, headerRange.end)}
                </Button>
                <Popover
                  open={Boolean(dateAnchor)}
                  anchorEl={dateAnchor}
                  onClose={() => setDateAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      component="input"
                      type="date"
                      value={draftRange.start}
                      onChange={(event) => setDraftRange((current) => ({ ...current, start: event.target.value }))}
                      sx={{ height: 38, borderRadius: '10px', border: '1px solid #e6e8ec', px: 1, fontFamily: 'inherit' }}
                    />
                    <Typography sx={{ color: '#9ca3af' }}>–</Typography>
                    <Box
                      component="input"
                      type="date"
                      value={draftRange.end}
                      onChange={(event) => setDraftRange((current) => ({ ...current, end: event.target.value }))}
                      sx={{ height: 38, borderRadius: '10px', border: '1px solid #e6e8ec', px: 1, fontFamily: 'inherit' }}
                    />
                    <Button
                      onClick={() => {
                        if (!draftRange.start || !draftRange.end || draftRange.start > draftRange.end) return;
                        setHeaderRange(draftRange);
                        setDateAnchor(null);
                      }}
                      sx={{ height: 38, px: 2, borderRadius: '10px', backgroundColor: '#111827', color: '#fff', textTransform: 'none', '&:hover': { backgroundColor: '#000' } }}
                    >
                      Apply
                    </Button>
                  </Box>
                </Popover>
                <Button
                  startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                  sx={{ ...headerButtonSx, display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  Export Report
                </Button>
              </>
            ) : null}
            <IconButton aria-label="Notifications" sx={{ color: '#111827' }}>
              <Badge variant="dot" color="error" overlap="circular" sx={{ '& .MuiBadge-dot': { backgroundColor: '#ef4444' } }}>
                <NotificationsNoneOutlinedIcon />
              </Badge>
            </IconButton>
            <IconButton onClick={handleMenuOpen} sx={{ color: '#2A2A2A', p: 0.5 }} aria-label="Account menu">
              {meProfile?.picture ? (
                <Box
                  component="img"
                  src={meProfile.picture}
                  alt=""
                  sx={{ height: 32, width: 32, borderRadius: '50%', objectFit: 'cover' }}
                />
              ) : (
                <Avatar
                  alt={formatUserName(meProfile) || meProfile?.email || 'User'}
                  sx={{ width: 32, height: 32, fontSize: 14 }}
                >
                  {(meProfile?.first_name?.[0] || meProfile?.email?.[0] || '?').toUpperCase()}
                </Avatar>
              )}
            </IconButton>
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleOpenProfile}>Profile</MenuItem>
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', border: '1px solid #eef0f3', boxShadow: 'none' } }}>
        <DialogTitle sx={{ fontSize: 18, fontWeight: 700, color: '#111827' }}>My profile</DialogTitle>
        <DialogContent dividers>
          {profileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : meProfile ? (
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                '& td': { wordBreak: 'break-word' },
              }}
            >
              <tbody>
                {[
                  ['First name', meProfile.first_name],
                  ['Last name', meProfile.last_name],
                  ['Email', meProfile.email],
                  ['Role', meProfile.role],
                  ['Account type', meProfile.account_type],
                  [
                    'Brand',
                    meProfile.brand_id && typeof meProfile.brand_id === 'object'
                      ? meProfile.brand_id.name
                      : meProfile.brand_id,
                  ],
                  [
                    'Created',
                    meProfile.created_at ? new Date(meProfile.created_at).toLocaleString() : '—',
                  ],
                  [
                    'Updated',
                    meProfile.updated_at ? new Date(meProfile.updated_at).toLocaleString() : '—',
                  ],
                ].map(([label, value]) => (
                  <tr key={label} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px', fontWeight: 700, width: '38%', color: '#555' }}>
                      {label}
                    </td>
                    <td style={{ padding: '10px' }}>{value ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
              Could not load profile. Check that you are logged in and try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)} sx={{ height: 36, px: 1.75, borderRadius: '10px', backgroundColor: '#111827', color: '#fff', textTransform: 'none', fontWeight: 600, '&:hover': { backgroundColor: '#000' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        component="nav"
        sx={{
          width: { md: drawerPaperWidth },
          flexShrink: { md: 0 },
        }}
      >
        <Drawer
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop ? true : mobileOpen}
          onClose={closeMobileDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: drawerPaperWidth,
              boxSizing: 'border-box',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawerNav}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${drawerPaperWidth}px)` },
          maxWidth: '100%',
          p: { xs: 2, sm: 3 },
          pt: { xs: 10, sm: 11 },
          background: '#f2f5f9',
          minHeight: '100vh',
          overflowX: 'auto',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

export default DashboardLayout;
